import axios from 'axios';
import { accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import type {
  ProductionQualityReport,
  CancelProductionOrderPayload,
  CompleteProductionOrderPayload,
  CreateProductionOrderPayload,
  ProducibleProduct,
  ProducibleProductQuery,
  ProductionEnvelope,
  ProductionMovement,
  ProductionOrder,
  ProductionOrderListItem,
  ProductionOrderListQuery,
  ProductionOrderSummary,
  ProductionOrderSummaryQuery,
  ProductionPagedResult,
  ProductionPreview,
  ProductionPreviewPayload,
  ProductionStockShortage,
  ProductionByProductScrapRow,
  ProductionByProductScrapTotals,
  ProductionConsumptionRow,
  ProductionConsumptionTotals,
  ProductionConversionByWorkCenterRow,
  ProductionConversionByWorkCenterTotals,
  ProductionCostVarianceRow,
  ProductionCostVarianceTotals,
  ProductionSettings,
  ProductionNegativeSemiRow,
  ProductionNegativeSemiTotals,
  ProductionReportPage,
  ProductionReportQuery,
  ProductionSummaryRow,
  ProductionSummaryTotals,
  ProductionTree,
  ProductionTreeQuery,
  ProductionWarehouse,
  UpdateProductionOrderPayload,
  WhereUsed,
} from '../types/production.types';

/**
 * Üretim fişi HTTP katmanı. CompanyId gönderilmez; şirket JWT'den çözülür.
 * Gövde alanları çağıran tarafta PascalCase kurulur ve olduğu gibi gider.
 */

export class ProductionApiError extends Error {
  code?: string;
  shortages?: ProductionStockShortage[];

  constructor(message: string, code?: string, shortages?: ProductionStockShortage[]) {
    super(message);
    this.name = 'ProductionApiError';
    this.code = code;
    this.shortages = shortages;
  }
}

const fallbackMessage = 'Beklenmeyen bir hata oluştu.';

function unwrap<T>(body: ProductionEnvelope<T> | undefined): T {
  if (!body || body.success === false || body.data === undefined) {
    throw new ProductionApiError(body?.message || fallbackMessage, body?.code, body?.shortages);
  }
  return body.data;
}

function toApiError(error: unknown): ProductionApiError {
  if (error instanceof ProductionApiError) return error;
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (data && typeof data === 'object') {
      const body = data as ProductionEnvelope<unknown>;
      return new ProductionApiError(body.message || fallbackMessage, body.code, body.shortages);
    }
  }
  return new ProductionApiError(fallbackMessage);
}

async function request<T>(run: () => Promise<{ data: ProductionEnvelope<T> }>): Promise<T> {
  try {
    const response = await run();
    return unwrap(response.data);
  } catch (error) {
    throw toApiError(error);
  }
}

function listParams(query: ProductionOrderListQuery): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  if (query.page != null) params.page = query.page;
  if (query.pageSize != null) params.pageSize = query.pageSize;
  if (query.search) params.search = query.search;
  if (query.status) params.status = query.status;
  if (query.productId) params.productId = query.productId;
  if (query.warehouseId) params.warehouseId = query.warehouseId;
  if (query.dateFrom) params.dateFrom = query.dateFrom;
  if (query.dateTo) params.dateTo = query.dateTo;
  if (query.rootOnly != null) params.rootOnly = query.rootOnly;
  if (query.sortColumn) params.sortColumn = query.sortColumn;
  if (query.sortDirection) params.sortDirection = query.sortDirection;
  return params;
}

export function getOrdersPaged(query: ProductionOrderListQuery = {}) {
  return request<ProductionPagedResult<ProductionOrderListItem>>(() =>
    accountingApi.get(ACCOUNTING_API.PRODUCTION_ORDERS_PAGED, { params: listParams(query) }));
}

export function getSummary(query: ProductionOrderSummaryQuery = {}) {
  const params: Record<string, string> = {};
  if (query.dateFrom) params.dateFrom = query.dateFrom;
  if (query.dateTo) params.dateTo = query.dateTo;
  if (query.warehouseId) params.warehouseId = query.warehouseId;
  return request<ProductionOrderSummary>(() =>
    accountingApi.get(ACCOUNTING_API.PRODUCTION_ORDERS_SUMMARY, { params }));
}

export function getOrder(id: string) {
  return request<ProductionOrder>(() => accountingApi.get(ACCOUNTING_API.PRODUCTION_ORDER(id)));
}

export interface ProductionLotUse {
  lotId?: string | null;
  itemName?: string | null;
  outputType?: string | null;
  lotNo?: string | null;
  expiryDate?: string | null;
  quantity: number;
  expired?: boolean;
}

export interface ProductionLotTrace {
  inputs: ProductionLotUse[];
  outputs: ProductionLotUse[];
  unassigned: ProductionLotUse[];
  warning?: string | null;
}

export function getOrderLots(id: string) {
  return request<ProductionLotTrace>(() => accountingApi.get(`${ACCOUNTING_API.PRODUCTION_ORDER(id)}/lots`));
}

export function preview(body: ProductionPreviewPayload) {
  return request<ProductionPreview>(() => accountingApi.post(ACCOUNTING_API.PRODUCTION_ORDER_PREVIEW, body));
}

export function createOrder(body: CreateProductionOrderPayload) {
  return request<ProductionOrder>(() => accountingApi.post(ACCOUNTING_API.PRODUCTION_ORDER_CREATE, body));
}

export function updateOrder(id: string, body: UpdateProductionOrderPayload) {
  return request<ProductionOrder>(() => accountingApi.post(ACCOUNTING_API.PRODUCTION_ORDER_UPDATE(id), body));
}

export function deleteOrder(id: string) {
  return request<boolean>(() => accountingApi.post(ACCOUNTING_API.PRODUCTION_ORDER_DELETE(id)));
}

export function completeOrder(id: string, body: CompleteProductionOrderPayload) {
  return request<ProductionOrder>(() => accountingApi.post(ACCOUNTING_API.PRODUCTION_ORDER_COMPLETE(id), body));
}

export function cancelOrder(id: string, body: CancelProductionOrderPayload) {
  return request<ProductionOrder>(() => accountingApi.post(ACCOUNTING_API.PRODUCTION_ORDER_CANCEL(id), body));
}

export function completeTree(id: string, body: CompleteProductionOrderPayload) {
  return request<ProductionOrder>(() => accountingApi.post(ACCOUNTING_API.PRODUCTION_ORDER_COMPLETE_TREE(id), body));
}

export function cancelTree(id: string, body: CancelProductionOrderPayload) {
  return request<ProductionOrder>(() => accountingApi.post(ACCOUNTING_API.PRODUCTION_ORDER_CANCEL_TREE(id), body));
}

export function regenerateChildren(id: string) {
  return request<ProductionOrder>(() => accountingApi.post(ACCOUNTING_API.PRODUCTION_ORDER_REGENERATE_CHILDREN(id)));
}

export function getMovements(id: string) {
  return request<ProductionMovement[]>(() => accountingApi.get(ACCOUNTING_API.PRODUCTION_ORDER_MOVEMENTS(id)));
}

export function getProducibleProducts(query: ProducibleProductQuery = {}) {
  const params: Record<string, string | number> = {};
  if (query.search) params.search = query.search;
  if (query.page != null) params.page = query.page;
  if (query.pageSize != null) params.pageSize = query.pageSize;
  if (query.productId) params.productId = query.productId;
  return request<ProductionPagedResult<ProducibleProduct>>(() =>
    accountingApi.get(ACCOUNTING_API.PRODUCTION_PRODUCIBLE_PRODUCTS, { params }));
}

export function getWarehouses() {
  return request<ProductionWarehouse[]>(() => accountingApi.get(ACCOUNTING_API.PRODUCTION_WAREHOUSES));
}

export function getTree(productId: string, query: ProductionTreeQuery = {}) {
  const params: Record<string, string | number> = {};
  if (query.quantity != null) params.quantity = query.quantity;
  if (query.branchId) params.branchId = query.branchId;
  return request<ProductionTree>(() => accountingApi.get(ACCOUNTING_API.PRODUCTION_TREE(productId), { params }));
}

export function getWhereUsed(itemId: string) {
  return request<WhereUsed>(() => accountingApi.get(ACCOUNTING_API.PRODUCTION_WHERE_USED(itemId)));
}

function reportParams(query: ProductionReportQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (query.page != null) params.page = query.page;
  if (query.pageSize != null) params.pageSize = query.pageSize;
  if (query.dateFrom) params.dateFrom = query.dateFrom;
  if (query.dateTo) params.dateTo = query.dateTo;
  if (query.warehouseId) params.warehouseId = query.warehouseId;
  if (query.productId) params.productId = query.productId;
  if (query.workCenterId) params.workCenterId = query.workCenterId;
  if (query.inputProductId) params.inputProductId = query.inputProductId;
  if (query.outputType) params.outputType = query.outputType;
  if (query.minAbsVariancePercent != null) params.minAbsVariancePercent = query.minAbsVariancePercent;
  if (query.sortColumn) params.sortColumn = query.sortColumn;
  if (query.sortDirection) params.sortDirection = query.sortDirection;
  return params;
}

export function getProductionSummary(query: ProductionReportQuery = {}) {
  return request<ProductionReportPage<ProductionSummaryRow, ProductionSummaryTotals>>(() =>
    accountingApi.get(ACCOUNTING_API.PRODUCTION_REPORT_SUMMARY, { params: reportParams(query) }));
}

export function getProductionConsumption(query: ProductionReportQuery = {}) {
  return request<ProductionReportPage<ProductionConsumptionRow, ProductionConsumptionTotals>>(() =>
    accountingApi.get(ACCOUNTING_API.PRODUCTION_REPORT_CONSUMPTION, { params: reportParams(query) }));
}

export function getProductionByProductScrap(query: ProductionReportQuery = {}) {
  return request<ProductionReportPage<ProductionByProductScrapRow, ProductionByProductScrapTotals>>(() =>
    accountingApi.get(ACCOUNTING_API.PRODUCTION_REPORT_BYPRODUCT_SCRAP, { params: reportParams(query) }));
}

export function getProductionCostVariance(query: ProductionReportQuery = {}) {
  return request<ProductionReportPage<ProductionCostVarianceRow, ProductionCostVarianceTotals>>(() =>
    accountingApi.get(ACCOUNTING_API.PRODUCTION_REPORT_COST_VARIANCE, { params: reportParams(query) }));
}

export function getProductionQuality(query: ProductionReportQuery = {}) {
  return request<ProductionQualityReport>(() =>
    accountingApi.get(ACCOUNTING_API.PRODUCTION_REPORT_QUALITY, { params: reportParams(query) }));
}

export function getProductionConversionByWorkCenter(query: ProductionReportQuery = {}) {
  return request<ProductionReportPage<ProductionConversionByWorkCenterRow, ProductionConversionByWorkCenterTotals>>(() =>
    accountingApi.get(ACCOUNTING_API.PRODUCTION_REPORT_CONVERSION, { params: reportParams(query) }));
}

export function getProductionSettings() {
  return request<ProductionSettings>(() => accountingApi.get(ACCOUNTING_API.PRODUCTION_SETTINGS));
}

export function saveProductionSettings(payload: {
  OverheadPercent: number;
  IncludeConversionInStandard: boolean;
  HoldLotsOnQualityWarning?: boolean;
}) {
  return request<ProductionSettings>(() => accountingApi.post(`${ACCOUNTING_API.PRODUCTION_SETTINGS}/save`, payload));
}

export function getProductionNegativeSemis(query: ProductionReportQuery = {}) {
  return request<ProductionReportPage<ProductionNegativeSemiRow, ProductionNegativeSemiTotals>>(() =>
    accountingApi.get(ACCOUNTING_API.PRODUCTION_REPORT_NEGATIVE_SEMIS, { params: reportParams(query) }));
}
